import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Mail,
  AlertTriangle,
  RefreshCw,
  Trash2,
  Plus,
  KeyRound,
  Inbox,
  ChevronDown
} from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import { Callout, EmptyState, Field, StatusPill } from '../../../shared/components/ui/primitives';
import toast from 'react-hot-toast';
import {
  acceptEmailLeadSuggestion,
  connectEmailLeadMailbox,
  createEmailLeadSource,
  deleteEmailLeadSource,
  disconnectEmailLeadMailbox,
  dismissEmailLeadSuggestion,
  getAllProjects,
  getEmailLeadImapPreset,
  getEmailLeadReusableSenders,
  getEmailLeadSettings,
  getEmailLeadSources,
  getEmailLeadSuggestions,
  getPreLeadLists,
  syncEmailLeadMailbox,
  updateEmailLeadMailbox
} from '../../services/api/implementation/impl';
import {
  EMAIL_LEAD_REUSABLE_SENDERS_QUERY_KEY,
  EMAIL_LEAD_SETTINGS_QUERY_KEY,
  EMAIL_LEAD_SOURCES_QUERY_KEY,
  EMAIL_LEAD_SUGGESTIONS_QUERY_KEY,
  describeSyncResult
} from '../../utils/emailLeadsAccess';
import { findSenderForAddress } from '../../utils/emailSendersAccess';

/**
 * Portal lead-email settings.
 *
 * Four things here are deliberate rather than decorative:
 *
 *  - The password field says "app password" everywhere and links to the page
 *    that generates one. Pasting the account password is the single most
 *    common setup failure on Gmail and Outlook, and the server's error for it
 *    is unhelpful, so the form says it before the attempt rather than after.
 *  - The IMAP host fills itself in from the address. Four fields of server
 *    configuration is where a non-technical builder abandons this screen.
 *  - Routing rules are shown in the same modal as the mailbox, because a
 *    connected mailbox with no rule and no default list ingests nothing — the
 *    two settings are only meaningful together.
 *  - Disconnect states what it keeps. Builders hesitate over it otherwise, and
 *    the answer ("your leads stay, the inbox link goes") is reassuring.
 */

const PORTAL_HINTS = {
  '99acres': 'Read by a dedicated parser — no AI credits are spent on these.',
  housing: 'Read by AI when the layout is not already labelled.',
  nobroker: 'Read by AI when the layout is not already labelled.',
  magicbricks: 'Read by AI when the layout is not already labelled.',
  squareyards: 'Read by AI when the layout is not already labelled.',
  unknown: 'Any sender — use this for brokers who forward enquiries by hand.'
};

const emptyMailboxForm = {
  label: '',
  imap_user: '',
  imap_password: '',
  imap_host: '',
  imap_port: 993,
  imap_secure: true,
  mailbox: 'INBOX',
  initial_lookback_days: 7,
  llm_fallback_enabled: true,
  default_pre_lead_list_id: ''
};

const emptyRuleForm = {
  portal: '99acres',
  name: '',
  match_from_address: '',
  match_subject_contains: '',
  match_body_contains: '',
  pre_lead_list_id: '',
  project_id: '',
  integration_id: '',
  priority: 0
};

function fieldClass() {
  return 'w-full rounded-control border border-hairline-strong bg-surface px-3 py-2 text-sm text-gray-900 read-only:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600';
}

const menuItemClass =
  'block w-full rounded-action px-3 py-2 text-left text-sm text-gray-800 hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600';

/** One status per inbox, in the portal's shared tone vocabulary. */
function mailboxStatus(mailbox) {
  if (!mailbox.isActive) return { tone: 'warning', label: 'Paused' };
  if (mailbox.lastError) return { tone: 'danger', label: 'Needs attention' };
  if (!mailbox.hasSynced) return { tone: 'neutral', label: 'Finishing setup' };
  return { tone: 'success', label: 'Active' };
}

/** Turn an axios error into the server's own message, which is written for builders. */
function errorText(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

/**
 * The settings themselves, without a modal around them — the Email & Inboxes
 * page shows them inline on its Lead Ingestion tab.
 *
 * `senderAddresses` (`[{ address, name, kind }]`, from useEmailSenders) lets
 * the Add inbox form say when the address being connected already has an app
 * password saved for sending: it is the same Gmail/Zoho app password, and
 * builders were not being told so.
 */
export function EmailLeadSettingsPanel({ senderAddresses = [], onDirtyChange, showIntro = true }) {
  const queryClient = useQueryClient();

  const [showMailboxForm, setShowMailboxForm] = useState(false);
  const [mailboxForm, setMailboxForm] = useState(emptyMailboxForm);
  const [presetHint, setPresetHint] = useState(null);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleForm, setRuleForm] = useState(emptyRuleForm);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(null);
  // Reuse the saved sender's password when the address matches one; opt-out, not opt-in.
  const [reusePassword, setReusePassword] = useState(true);
  // Set when the form is re-entering the password for an inbox already connected.
  const [reconnecting, setReconnecting] = useState(false);
  const [menuFor, setMenuFor] = useState(null);
  const menuRef = useRef(null);
  const [suggestionListIds, setSuggestionListIds] = useState({});
  const [editingMailboxId, setEditingMailboxId] = useState(null);
  const [mailboxEdit, setMailboxEdit] = useState({
    default_pre_lead_list_id: '',
    llm_fallback_enabled: true
  });

  const {
    data: settings,
    isLoading,
    isError: settingsFailed,
    error: settingsError
  } = useQuery({
    queryKey: EMAIL_LEAD_SETTINGS_QUERY_KEY,
    queryFn: getEmailLeadSettings,
    retry: false
  });

  const { data: sourceData } = useQuery({
    queryKey: EMAIL_LEAD_SOURCES_QUERY_KEY,
    queryFn: getEmailLeadSources,
    retry: false
  });

  const { data: suggestionData } = useQuery({
    queryKey: EMAIL_LEAD_SUGGESTIONS_QUERY_KEY,
    queryFn: getEmailLeadSuggestions,
    retry: false
  });

  const { data: preLeadListData } = useQuery({
    queryKey: ['preLeadLists'],
    queryFn: getPreLeadLists,
    retry: false
  });

  const { data: projects } = useQuery({
    queryKey: ['allProjects'],
    queryFn: getAllProjects,
    retry: false
  });

  const { data: reusableSenders } = useQuery({
    queryKey: EMAIL_LEAD_REUSABLE_SENDERS_QUERY_KEY,
    queryFn: getEmailLeadReusableSenders,
    retry: false,
    // Also marks connected inboxes that share a sender's password, so it loads
    // for everyone who manages them, not only while the form is open.
    enabled: settings?.canManageMailbox === true
  });

  // Close the Options menu on an outside click or Escape.
  useEffect(() => {
    if (menuFor == null) return undefined;
    const onPointer = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuFor(null);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setMenuFor(null);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuFor]);

  /**
   * A failed request is not a permission verdict.
   *
   * `canManageMailbox` comes back false when the call fails simply because
   * there is no response to read it from — which rendered the "only the
   * account owner can connect an inbox" notice below and hid every control,
   * sending the reader off to check roles when the real cause was a 500. The
   * two states are now distinguished, and the server's own message is shown.
   */
  const mailboxes = settings?.mailboxes ?? [];
  const sources = sourceData?.sources ?? [];
  const suggestions = suggestionData?.suggestions ?? [];
  const canManageMailbox = settings?.canManageMailbox === true;
  const canManageSources = settings?.canManageSources === true;
  const preLeadLists = preLeadListData?.preLeadList ?? [];
  const portals = settings?.portals ?? [];

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: EMAIL_LEAD_SETTINGS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: EMAIL_LEAD_SOURCES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: EMAIL_LEAD_SUGGESTIONS_QUERY_KEY });
  };

  /**
   * A mailbox with no rule and no default list reads mail and ingests nothing.
   * That is a silent no-op, so it is called out at the top of the modal.
   */
  const unroutedMailboxes = useMemo(
    () =>
      mailboxes.filter(
        (mailbox) =>
          !mailbox.defaultPreLeadListId &&
          !sources.some(
            (source) =>
              source.isActive &&
              (source.integrationId === null || source.integrationId === mailbox.id)
          )
      ),
    [mailboxes, sources]
  );

  // Fill the IMAP server in from the address, so the builder types one field.
  useEffect(() => {
    const address = mailboxForm.imap_user.trim();
    if (!address.includes('@')) {
      setPresetHint(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      const preset = await getEmailLeadImapPreset(address);
      if (cancelled) return;
      setPresetHint(preset);
      if (!preset) return;
      setMailboxForm((current) => ({
        ...current,
        // Never overwrite a host the builder typed themselves.
        imap_host: current.imap_host || preset.host,
        imap_port: current.imap_port || preset.port,
        imap_secure: preset.secure
      }));
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [mailboxForm.imap_user]);

  /**
   * A saved sender whose SMTP login is this address. The server holds its
   * password, so reusing it sends `reuse_sender` and no password at all.
   * `senderAddresses` is only a fallback hint for a backend without the
   * reusable-senders endpoint: it can say "same password", not fill it in.
   */
  const reusableSender = findSenderForAddress(reusableSenders, mailboxForm.imap_user);
  const matchingSender = reusableSender
    ? null
    : findSenderForAddress(senderAddresses, mailboxForm.imap_user);
  const reusing = Boolean(reusableSender) && reusePassword;

  const connectMutation = useMutation({
    mutationFn: () => {
      const { imap_password: typedPassword, ...rest } = mailboxForm;
      return connectEmailLeadMailbox({
        ...rest,
        ...(reusing
          ? {
              reuse_sender: {
                kind: reusableSender.kind,
                company_id: reusableSender.companyId ?? null
              }
            }
          : { imap_password: typedPassword }),
        imap_port: Number(mailboxForm.imap_port) || 993,
        initial_lookback_days: Number(mailboxForm.initial_lookback_days) || 7,
        default_pre_lead_list_id: mailboxForm.default_pre_lead_list_id
          ? Number(mailboxForm.default_pre_lead_list_id)
          : null
      });
    },
    onSuccess: () => {
      toast.success(
        reconnecting
          ? 'App password updated'
          : 'Inbox connected — leads will start arriving within 10 minutes'
      );
      closeMailboxForm();
      refreshAll();
    },
    onError: (err) => toast.error(errorText(err, 'Could not connect this inbox'))
  });

  const syncMutation = useMutation({
    mutationFn: (mailboxId) => syncEmailLeadMailbox(mailboxId),
    onSuccess: (stats) => {
      toast.success(describeSyncResult(stats));
      refreshAll();
      queryClient.invalidateQueries({ queryKey: ['emailLeadMessages'] });
    },
    onError: (err) => {
      toast.error(errorText(err, 'Sync failed'));
      refreshAll();
    }
  });

  const toggleMutation = useMutation({
    mutationFn: ({ mailboxId, payload }) => updateEmailLeadMailbox(mailboxId, payload),
    onSuccess: () => {
      toast.success('Inbox updated');
      refreshAll();
    },
    onError: (err) => toast.error(errorText(err, 'Could not update this inbox'))
  });

  const disconnectMutation = useMutation({
    mutationFn: (mailboxId) => disconnectEmailLeadMailbox(mailboxId),
    onSuccess: () => {
      toast.success('Inbox disconnected');
      setConfirmingDisconnect(null);
      refreshAll();
    },
    onError: (err) => toast.error(errorText(err, 'Could not disconnect this inbox'))
  });

  const createRuleMutation = useMutation({
    mutationFn: () =>
      createEmailLeadSource({
        ...ruleForm,
        pre_lead_list_id: Number(ruleForm.pre_lead_list_id),
        project_id: ruleForm.project_id ? Number(ruleForm.project_id) : null,
        integration_id: ruleForm.integration_id ? Number(ruleForm.integration_id) : null,
        priority: Number(ruleForm.priority) || 0
      }),
    onSuccess: () => {
      toast.success('Rule added');
      setRuleForm(emptyRuleForm);
      setShowRuleForm(false);
      refreshAll();
    },
    onError: (err) => toast.error(errorText(err, 'Could not save this rule'))
  });

  const acceptSuggestionMutation = useMutation({
    mutationFn: ({ suggestionId, preLeadListId, projectId }) =>
      acceptEmailLeadSuggestion(suggestionId, {
        pre_lead_list_id: preLeadListId,
        project_id: projectId ?? null,
        reprocess: true
      }),
    onSuccess: (result) => {
      // Say what accepting actually did. "Rule created" alone leaves the
      // builder wondering whether the mail already sitting in the list was
      // picked up, which is the whole reason they accepted it.
      toast.success(
        result?.reprocessed
          ? `Rule created — ${result.reprocessed} earlier email${
              result.reprocessed === 1 ? '' : 's'
            } turned into leads`
          : 'Rule created'
      );
      refreshAll();
      queryClient.invalidateQueries({ queryKey: ['emailLeadMessages'] });
    },
    onError: (err) => toast.error(errorText(err, 'Could not create this rule'))
  });

  const dismissSuggestionMutation = useMutation({
    mutationFn: (suggestionId) => dismissEmailLeadSuggestion(suggestionId),
    onSuccess: () => {
      toast.success('Suggestion dismissed');
      refreshAll();
    },
    onError: (err) => toast.error(errorText(err, 'Could not dismiss this suggestion'))
  });

  const deleteRuleMutation = useMutation({
    mutationFn: (sourceId) => deleteEmailLeadSource(sourceId),
    onSuccess: () => {
      toast.success('Rule deleted');
      refreshAll();
    },
    onError: (err) => toast.error(errorText(err, 'Could not delete this rule'))
  });

  const busy =
    connectMutation.isPending ||
    syncMutation.isPending ||
    toggleMutation.isPending ||
    disconnectMutation.isPending;

  const canSubmitMailbox =
    mailboxForm.label.trim() &&
    mailboxForm.imap_user.trim().includes('@') &&
    (reusing || mailboxForm.imap_password) &&
    mailboxForm.imap_host.trim();

  const canSubmitRule =
    ruleForm.name.trim() &&
    ruleForm.pre_lead_list_id &&
    (ruleForm.portal !== 'unknown' ||
      ruleForm.match_from_address.trim() ||
      ruleForm.match_subject_contains.trim() ||
      ruleForm.match_body_contains.trim());

  /**
   * Open the connect form — blank, or for an inbox already connected, which is
   * how its app password gets replaced: connecting the same address and folder
   * again updates that inbox rather than adding a second one. Its current
   * settings ride along so the reconnect does not reset them.
   */
  function openMailboxForm(mailbox) {
    // Replacing a password means typing the new one; reuse stays one click away.
    setReusePassword(!mailbox);
    setReconnecting(Boolean(mailbox));
    setMailboxForm(
      mailbox
        ? {
            ...emptyMailboxForm,
            label: mailbox.label,
            imap_user: mailbox.mailboxAddress ?? '',
            imap_host: mailbox.imapHost,
            imap_port: mailbox.imapPort,
            imap_secure: mailbox.imapSecure,
            mailbox: mailbox.mailbox,
            initial_lookback_days: mailbox.initialLookbackDays ?? 7,
            llm_fallback_enabled: mailbox.llmFallbackEnabled,
            default_pre_lead_list_id: mailbox.defaultPreLeadListId ?? ''
          }
        : emptyMailboxForm
    );
    setShowMailboxForm(true);
  }

  function closeMailboxForm() {
    setMailboxForm(emptyMailboxForm);
    setReusePassword(true);
    setReconnecting(false);
    setShowMailboxForm(false);
  }

  const isDirty = showMailboxForm || showRuleForm;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <div className="space-y-6">
      {showIntro ? (
        <div className="flex items-start gap-3">
          <div className="rounded-control bg-brand-100 p-2 text-brand-800">
            <Mail className="h-6 w-6" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Portal Lead Emails</h2>
            <p className="text-sm text-gray-600">
              Connect the inbox your 99acres, Housing.com and NoBroker alerts arrive in.
            </p>
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : settingsFailed ? (
        <Callout tone="danger" title="Could not load your inbox settings">
          {errorText(settingsError, 'The server did not respond.')} This is a server error, not a
          permission problem.
        </Callout>
      ) : (
        <>
          {settings && !canManageMailbox ? (
            <Callout tone="warning">
              Only the account owner or a Management user can connect an inbox — its password opens
              the whole mailbox. You can still see the leads it brings in.
            </Callout>
          ) : null}

          {unroutedMailboxes.length > 0 ? (
            <Callout tone="warning" icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}>
              {unroutedMailboxes.map((mailbox) => mailbox.label).join(', ')} has no rule and no
              default list, so nothing from it becomes a lead. Add a rule, or set a default list in
              Options.
            </Callout>
          ) : null}

          {/* Connected inboxes */}
          <section className="space-y-3" aria-labelledby="email-inboxes-heading">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 id="email-inboxes-heading" className="text-base font-semibold text-gray-900">
                  Connected inboxes
                </h3>
                <p className="text-xs text-gray-500">Where your portal lead alerts arrive.</p>
              </div>
              {canManageMailbox && !showMailboxForm ? (
                <Button variant="secondary" onClick={() => openMailboxForm()}>
                  <Plus className="h-4 w-4" aria-hidden="true" /> Add inbox
                </Button>
              ) : null}
            </div>

            {mailboxes.length === 0 && !showMailboxForm ? (
              <EmptyState title="No inbox connected yet." />
            ) : null}

            {mailboxes.map((mailbox) => {
              const status = mailboxStatus(mailbox);
              const sharedWith = findSenderForAddress(
                [...(reusableSenders ?? []), ...senderAddresses],
                mailbox.mailboxAddress
              );
              return (
                <div
                  key={mailbox.id}
                  className="rounded-panel border border-hairline bg-surface p-4 shadow-card">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-control bg-brand-100 text-brand-800">
                      <Inbox className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-gray-900">{mailbox.label}</p>
                        <StatusPill tone={status.tone} dense>
                          {status.label}
                        </StatusPill>
                      </div>
                      <p className="truncate text-sm text-gray-700">
                        {mailbox.mailboxAddress} · {mailbox.imapHost}:{mailbox.imapPort} · folder{' '}
                        {mailbox.mailbox}
                      </p>
                      <p className="text-xs text-gray-500">
                        {mailbox.lastSuccessAt
                          ? `Last read ${new Date(mailbox.lastSuccessAt).toLocaleString()}`
                          : 'First read runs within 10 minutes'}
                        {mailbox.llmFallbackEnabled ? ' · AI reading on' : ' · AI reading off'}
                      </p>
                      {sharedWith ? (
                        <p className="flex items-center gap-1.5 pt-0.5 text-xs text-brand-800">
                          <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
                          App password shared with{' '}
                          {sharedWith.kind === 'company' ? sharedWith.name : 'your personal sender'}
                        </p>
                      ) : null}
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <Button
                        variant="secondary"
                        disabled={busy}
                        isLoading={syncMutation.isPending && syncMutation.variables === mailbox.id}
                        onClick={() => syncMutation.mutate(mailbox.id)}>
                        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Sync now
                      </Button>
                      {canManageMailbox ? (
                        <>
                          <Button
                            variant="secondary"
                            disabled={busy}
                            onClick={() =>
                              toggleMutation.mutate({
                                mailboxId: mailbox.id,
                                payload: { is_active: !mailbox.isActive }
                              })
                            }>
                            {mailbox.isActive ? 'Pause' : 'Resume'}
                          </Button>
                          <div
                            className="relative"
                            ref={menuFor === mailbox.id ? menuRef : undefined}>
                            <Button
                              variant="secondary"
                              disabled={busy}
                              onClick={() =>
                                setMenuFor(menuFor === mailbox.id ? null : mailbox.id)
                              }>
                              Options
                              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                            </Button>
                            {menuFor === mailbox.id ? (
                              <div
                                role="menu"
                                aria-label={`Options for ${mailbox.label}`}
                                className="absolute right-0 z-20 mt-1 w-60 rounded-control border border-hairline bg-surface p-1 shadow-overlay">
                                <button
                                  type="button"
                                  role="menuitem"
                                  className={menuItemClass}
                                  onClick={() => {
                                    setMenuFor(null);
                                    setConfirmingDisconnect(null);
                                    setEditingMailboxId(mailbox.id);
                                    setMailboxEdit({
                                      default_pre_lead_list_id: mailbox.defaultPreLeadListId ?? '',
                                      llm_fallback_enabled: mailbox.llmFallbackEnabled
                                    });
                                  }}>
                                  Default list for unmatched mail…
                                </button>
                                <button
                                  type="button"
                                  role="menuitem"
                                  className={menuItemClass}
                                  onClick={() => {
                                    setMenuFor(null);
                                    toggleMutation.mutate({
                                      mailboxId: mailbox.id,
                                      payload: { llm_fallback_enabled: !mailbox.llmFallbackEnabled }
                                    });
                                  }}>
                                  {mailbox.llmFallbackEnabled
                                    ? 'Turn AI reading off'
                                    : 'Turn AI reading on'}
                                </button>
                                <button
                                  type="button"
                                  role="menuitem"
                                  className={menuItemClass}
                                  onClick={() => {
                                    setMenuFor(null);
                                    openMailboxForm(mailbox);
                                  }}>
                                  Update app password…
                                </button>
                              </div>
                            ) : null}
                          </div>
                          <Button
                            variant="danger-outline"
                            disabled={busy}
                            onClick={() => setConfirmingDisconnect(mailbox)}>
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">Disconnect {mailbox.label}</span>
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>

                  {mailbox.lastError ? (
                    <Callout
                      tone="danger"
                      className="mt-3"
                      icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}>
                      {mailbox.lastError}
                    </Callout>
                  ) : null}

                  {editingMailboxId === mailbox.id && canManageMailbox ? (
                    <div className="mt-3 space-y-3 rounded-control border border-hairline bg-surface-muted p-3">
                      <Field
                        label="Default list for unmatched mail"
                        htmlFor={`default-list-${mailbox.id}`}
                        hint="Mail no rule matched lands here.">
                        <select
                          id={`default-list-${mailbox.id}`}
                          className={fieldClass()}
                          value={mailboxEdit.default_pre_lead_list_id}
                          onChange={(e) =>
                            setMailboxEdit({
                              ...mailboxEdit,
                              default_pre_lead_list_id: e.target.value
                            })
                          }>
                          <option value="">Do not ingest unmatched mail</option>
                          {preLeadLists.map((list) => (
                            <option key={list.id} value={list.id}>
                              {list.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          isLoading={toggleMutation.isPending}
                          onClick={() =>
                            toggleMutation.mutate(
                              {
                                mailboxId: mailbox.id,
                                payload: {
                                  default_pre_lead_list_id: mailboxEdit.default_pre_lead_list_id
                                    ? Number(mailboxEdit.default_pre_lead_list_id)
                                    : null
                                }
                              },
                              { onSuccess: () => setEditingMailboxId(null) }
                            )
                          }>
                          Save
                        </Button>
                        <Button variant="secondary" onClick={() => setEditingMailboxId(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}

            {showMailboxForm && canManageMailbox ? (
              <div className="space-y-4 rounded-panel border border-hairline bg-surface p-4 shadow-card">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {reconnecting ? `Update app password — ${mailboxForm.label}` : 'Add inbox'}
                  </h4>
                </div>

                {/* Decoy fields absorb Chrome/Safari password autofill. */}
                <div aria-hidden="true" className="hidden">
                  <input type="text" name="username" autoComplete="username" tabIndex={-1} />
                  <input
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    tabIndex={-1}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="Label" htmlFor="imap-label">
                    <input
                      id="imap-label"
                      className={fieldClass()}
                      value={mailboxForm.label}
                      placeholder="Sales inbox"
                      onChange={(e) => setMailboxForm({ ...mailboxForm, label: e.target.value })}
                    />
                  </Field>
                  <Field label="Email address" htmlFor="imap-user">
                    <input
                      id="imap-user"
                      className={fieldClass()}
                      autoComplete="off"
                      readOnly={reconnecting}
                      value={mailboxForm.imap_user}
                      placeholder="sales@yourcompany.com"
                      onChange={(e) =>
                        setMailboxForm({ ...mailboxForm, imap_user: e.target.value })
                      }
                    />
                  </Field>
                </div>

                {reusableSender ? (
                  <fieldset className="space-y-2 rounded-control border border-brand-100 bg-brand-50 px-3 py-3">
                    <legend className="flex items-center gap-1.5 px-1 text-xs font-semibold text-brand-800">
                      <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
                      An app password is already saved for {reusableSender.address}
                    </legend>
                    <label className="flex items-start gap-2 text-sm text-gray-800">
                      <input
                        type="radio"
                        name="imap-password-source"
                        className="mt-1 accent-brand-700"
                        checked={reusePassword}
                        onChange={() => setReusePassword(true)}
                      />
                      <span>
                        Reuse the password saved for sending
                        {reusableSender.kind === 'company'
                          ? ` as ${reusableSender.name}`
                          : ' from your own account'}
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-800">
                      <input
                        type="radio"
                        name="imap-password-source"
                        className="mt-1 accent-brand-700"
                        checked={!reusePassword}
                        onChange={() => setReusePassword(false)}
                      />
                      <span>Use a different app password</span>
                    </label>
                  </fieldset>
                ) : matchingSender ? (
                  <Callout tone="brand" icon={<KeyRound className="h-4 w-4" aria-hidden="true" />}>
                    {matchingSender.address} already sends email
                    {matchingSender.kind === 'company' ? ` as ${matchingSender.name}` : ''} — paste
                    that same app password here.
                  </Callout>
                ) : null}

                {reusing ? null : (
                  <Field
                    label="App password"
                    htmlFor="imap-app-password"
                    hint={
                      <>
                        Not your normal password.
                        {presetHint?.appPasswordUrl ? (
                          <>
                            {' '}
                            <a
                              href={presetHint.appPasswordUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-brand-700 underline">
                              Generate one for {presetHint.label}
                            </a>
                            .
                          </>
                        ) : null}
                      </>
                    }>
                    <input
                      id="imap-app-password"
                      type="password"
                      name="imap-app-password"
                      autoComplete="new-password"
                      className={fieldClass()}
                      value={mailboxForm.imap_password}
                      onChange={(e) =>
                        setMailboxForm({ ...mailboxForm, imap_password: e.target.value })
                      }
                    />
                  </Field>
                )}

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Field label="IMAP server" htmlFor="imap-host">
                    <input
                      id="imap-host"
                      className={fieldClass()}
                      value={mailboxForm.imap_host}
                      placeholder="imap.gmail.com"
                      onChange={(e) =>
                        setMailboxForm({ ...mailboxForm, imap_host: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Port" htmlFor="imap-port">
                    <input
                      id="imap-port"
                      inputMode="numeric"
                      className={fieldClass()}
                      value={mailboxForm.imap_port}
                      onChange={(e) =>
                        setMailboxForm({ ...mailboxForm, imap_port: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Folder" htmlFor="imap-folder">
                    <input
                      id="imap-folder"
                      className={fieldClass()}
                      readOnly={reconnecting}
                      value={mailboxForm.mailbox}
                      onChange={(e) => setMailboxForm({ ...mailboxForm, mailbox: e.target.value })}
                    />
                  </Field>
                </div>

                {reconnecting ? null : (
                  <>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <Field
                        label="First read covers"
                        htmlFor="imap-lookback"
                        hint="Only for the first read.">
                        <select
                          id="imap-lookback"
                          className={fieldClass()}
                          value={mailboxForm.initial_lookback_days}
                          onChange={(e) =>
                            setMailboxForm({
                              ...mailboxForm,
                              initial_lookback_days: e.target.value
                            })
                          }>
                          <option value={1}>Last 1 day</option>
                          <option value={7}>Last 7 days</option>
                          <option value={30}>Last 30 days</option>
                          <option value={90}>Last 90 days</option>
                        </select>
                      </Field>
                      <Field label="Default list for unmatched mail" htmlFor="imap-default-list">
                        <select
                          id="imap-default-list"
                          className={fieldClass()}
                          value={mailboxForm.default_pre_lead_list_id}
                          onChange={(e) =>
                            setMailboxForm({
                              ...mailboxForm,
                              default_pre_lead_list_id: e.target.value
                            })
                          }>
                          <option value="">Do not ingest unmatched mail</option>
                          {preLeadLists.map((list) => (
                            <option key={list.id} value={list.id}>
                              {list.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    <label className="flex items-start gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="mt-1 accent-brand-700"
                        checked={mailboxForm.llm_fallback_enabled}
                        onChange={(e) =>
                          setMailboxForm({
                            ...mailboxForm,
                            llm_fallback_enabled: e.target.checked
                          })
                        }
                      />
                      <span>
                        Use AI for layouts we don&apos;t recognise
                        <span className="block text-xs text-gray-500">
                          99acres never needs it. Uses your AI credits.
                        </span>
                      </span>
                    </label>
                  </>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="primary"
                    disabled={!canSubmitMailbox || connectMutation.isPending}
                    isLoading={connectMutation.isPending}
                    onClick={() => connectMutation.mutate()}>
                    {reconnecting ? 'Save password' : 'Connect inbox'}
                  </Button>
                  <Button variant="secondary" onClick={closeMailboxForm}>
                    Cancel
                  </Button>
                  <span className="text-xs text-gray-500">
                    We sign in to check it before saving.
                  </span>
                </div>
              </div>
            ) : null}
          </section>

          {/* Rules the system worked out, waiting to be agreed */}
          {suggestions.length > 0 && canManageSources ? (
            <section className="space-y-3" aria-labelledby="email-nowhere-heading">
              <div>
                <h3 id="email-nowhere-heading" className="text-base font-semibold text-gray-900">
                  Mail with nowhere to go
                </h3>
                <p className="text-xs text-gray-500">
                  Pick a list and the rule writes itself — including for the emails already here.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {suggestions.map((suggestion) => {
                  const chosenList =
                    suggestionListIds[suggestion.id] ?? suggestion.suggestedPreLeadListId ?? '';
                  const selectId = `suggestion-list-${suggestion.id}`;
                  return (
                    <div
                      key={suggestion.id}
                      className="flex flex-col gap-3 rounded-panel border border-brand-100 bg-brand-50 p-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {suggestion.description}
                        </p>
                        {suggestion.sampleContactName || suggestion.sampleSubject ? (
                          <p className="mt-0.5 truncate text-xs text-gray-600">
                            Latest: {suggestion.sampleContactName ?? ''}
                            {suggestion.sampleContactName && suggestion.sampleSubject ? ' — ' : ''}
                            {suggestion.sampleSubject ?? ''}
                          </p>
                        ) : null}
                      </div>
                      <Field label="Send these to" htmlFor={selectId} size="caption">
                        <select
                          id={selectId}
                          className={fieldClass()}
                          value={chosenList}
                          onChange={(e) =>
                            setSuggestionListIds({
                              ...suggestionListIds,
                              [suggestion.id]: e.target.value
                            })
                          }>
                          <option value="">Choose a pre-lead list</option>
                          {preLeadLists.map((list) => (
                            <option key={list.id} value={list.id}>
                              {list.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <div className="mt-auto grid grid-cols-2 gap-2">
                        <Button
                          variant="primary"
                          disabled={!chosenList || acceptSuggestionMutation.isPending}
                          isLoading={
                            acceptSuggestionMutation.isPending &&
                            acceptSuggestionMutation.variables?.suggestionId === suggestion.id
                          }
                          onClick={() =>
                            acceptSuggestionMutation.mutate({
                              suggestionId: suggestion.id,
                              preLeadListId: Number(chosenList),
                              projectId: suggestion.suggestedProjectId
                            })
                          }>
                          Create rule
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => dismissSuggestionMutation.mutate(suggestion.id)}>
                          Not a lead source
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          {/* Routing rules */}
          <section className="space-y-3" aria-labelledby="email-rules-heading">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 id="email-rules-heading" className="text-base font-semibold text-gray-900">
                  Where leads go
                </h3>
                <p className="text-xs text-gray-500">
                  One rule per portal, or one per project using &quot;body contains&quot;.
                </p>
              </div>
              {canManageSources && !showRuleForm ? (
                <Button variant="secondary" onClick={() => setShowRuleForm(true)}>
                  <Plus className="h-4 w-4" aria-hidden="true" /> Add rule
                </Button>
              ) : null}
            </div>

            {sources.length === 0 && !showRuleForm ? <EmptyState title="No rules yet." /> : null}

            {sources.length > 0 ? (
              <div className="divide-y divide-hairline overflow-hidden rounded-panel border border-hairline bg-surface">
                {sources.map((source) => (
                  <div
                    key={source.id}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">{source.name}</p>
                        <StatusPill tone="neutral" dense>
                          {source.portalLabel}
                        </StatusPill>
                        {!source.isActive ? (
                          <StatusPill tone="warning" dense>
                            Off
                          </StatusPill>
                        ) : null}
                      </div>
                      <p className="text-xs text-gray-600">
                        → {source.preLeadListName ?? `list #${source.preLeadListId}`}
                        {source.leadCount ? ` · ${source.leadCount} leads so far` : ''}
                        {[
                          source.matchFromAddress ? `from ${source.matchFromAddress}` : null,
                          source.matchSubjectContains
                            ? `subject has "${source.matchSubjectContains}"`
                            : null,
                          source.matchBodyContains ? `body has "${source.matchBodyContains}"` : null
                        ]
                          .filter(Boolean)
                          .map((part) => ` · ${part}`)
                          .join('')}
                      </p>
                    </div>
                    {canManageSources ? (
                      <Button
                        variant="danger-outline"
                        isLoading={
                          deleteRuleMutation.isPending && deleteRuleMutation.variables === source.id
                        }
                        onClick={() => deleteRuleMutation.mutate(source.id)}>
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                        <span className="sr-only">Delete rule {source.name}</span>
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {showRuleForm && canManageSources ? (
              <div className="space-y-4 rounded-panel border border-hairline bg-surface p-4 shadow-card">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="Rule name" htmlFor="rule-name">
                    <input
                      id="rule-name"
                      className={fieldClass()}
                      value={ruleForm.name}
                      placeholder="99acres → Acme Heights"
                      onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                    />
                  </Field>
                  <Field label="Portal" htmlFor="rule-portal" hint={PORTAL_HINTS[ruleForm.portal]}>
                    <select
                      id="rule-portal"
                      className={fieldClass()}
                      value={ruleForm.portal}
                      onChange={(e) => setRuleForm({ ...ruleForm, portal: e.target.value })}>
                      {portals.map((portal) => (
                        <option key={portal.value} value={portal.value}>
                          {portal.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="Send leads to" htmlFor="rule-list">
                    <select
                      id="rule-list"
                      className={fieldClass()}
                      value={ruleForm.pre_lead_list_id}
                      onChange={(e) =>
                        setRuleForm({ ...ruleForm, pre_lead_list_id: e.target.value })
                      }>
                      <option value="">Choose a pre-lead list</option>
                      {preLeadLists.map((list) => (
                        <option key={list.id} value={list.id}>
                          {list.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field
                    label="Project (optional)"
                    htmlFor="rule-project"
                    hint="Assigns the lead to that project's sales owner.">
                    <select
                      id="rule-project"
                      className={fieldClass()}
                      value={ruleForm.project_id}
                      onChange={(e) => setRuleForm({ ...ruleForm, project_id: e.target.value })}>
                      <option value="">No project</option>
                      {(projects ?? []).map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Field label="Sender is (optional)" htmlFor="rule-from">
                    <input
                      id="rule-from"
                      className={fieldClass()}
                      value={ruleForm.match_from_address}
                      placeholder="99acres.com"
                      onChange={(e) =>
                        setRuleForm({ ...ruleForm, match_from_address: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Subject contains (optional)" htmlFor="rule-subject">
                    <input
                      id="rule-subject"
                      className={fieldClass()}
                      value={ruleForm.match_subject_contains}
                      onChange={(e) =>
                        setRuleForm({ ...ruleForm, match_subject_contains: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Body contains (optional)" htmlFor="rule-body">
                    <input
                      id="rule-body"
                      className={fieldClass()}
                      value={ruleForm.match_body_contains}
                      placeholder="Acme Heights"
                      onChange={(e) =>
                        setRuleForm({ ...ruleForm, match_body_contains: e.target.value })
                      }
                    />
                  </Field>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    disabled={!canSubmitRule || createRuleMutation.isPending}
                    isLoading={createRuleMutation.isPending}
                    onClick={() => createRuleMutation.mutate()}>
                    Save rule
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setRuleForm(emptyRuleForm);
                      setShowRuleForm(false);
                    }}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}
          </section>
        </>
      )}

      {confirmingDisconnect ? (
        <Modal name="Disconnect inbox?" onClose={() => setConfirmingDisconnect(null)} width={34}>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Housingram will stop reading <strong>{confirmingDisconnect.mailboxAddress}</strong>.
              Leads already created stay in your pre-lead lists.
            </p>
            {findSenderForAddress(
              [...(reusableSenders ?? []), ...senderAddresses],
              confirmingDisconnect.mailboxAddress
            ) ? (
              <Callout tone="brand">
                Sending from this address keeps working — its saved password is separate.
              </Callout>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmingDisconnect(null)}>
                Keep it
              </Button>
              <Button
                variant="danger"
                isLoading={disconnectMutation.isPending}
                onClick={() => disconnectMutation.mutate(confirmingDisconnect.id)}>
                Disconnect
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/** The same settings in a modal, for the pages that still open it that way. */
export default function EmailLeadSettingsModal({ onClose, senderAddresses }) {
  const [isDirty, setIsDirty] = useState(false);
  return (
    <Modal isDirty={isDirty} confirmOnClose name="Portal Lead Emails" onClose={onClose} width={62}>
      <EmailLeadSettingsPanel senderAddresses={senderAddresses} onDirtyChange={setIsDirty} />
    </Modal>
  );
}
